import React from 'react';

import { Header, Inspector, TreeList } from '../components';
import {
  ContextValue,
  EditorComponentsProvider,
  EditorContextProvider,
} from '../contexts';
import { EditorInfoContextProvider, EditorInfoContextValue } from '../contexts/external';
import { useResizerSidebar } from '../hooks/useResizerSidebar';
import { DocumentComponents, DocumentNode } from '../types';
import { defaultComponents } from './defaultComponents';
import * as S from './Editor.style';

export interface EditorProps {
  width: number;
  siderWidth?: number;
  className?: string;
  value: DocumentNode;
  onChange: (value: DocumentNode) => void;
  newValue: DocumentNode;
  components?: DocumentComponents;
  disableDefaultComponents?: boolean;
  disableComponentIds?: string[];
  editorInfoContext: EditorInfoContextValue;
  unitContext: ContextValue;
  displayDate?: Date;
}

export function Editor(props: EditorProps): React.ReactElement {
  const {
    width,
    siderWidth,
    className,
    value,
    onChange,
    newValue,
    components,
    disableDefaultComponents,
    disableComponentIds = [],
    editorInfoContext,
    unitContext,
    displayDate = new Date(),
  } = props;

  const [rightSidebarRef, startRightSidebarResizing, rightSidebarWidth] =
    useResizerSidebar('right', siderWidth);

  const [leftSidebarRef, startLeftSidebarResizing, leftSidebarWidth] =
    useResizerSidebar('left', siderWidth);

  const filteredDefaultComponents = React.useMemo(() => {
    disableComponentIds.forEach((id: string) => {
      delete defaultComponents[id];
    });

    return defaultComponents;
  }, [disableComponentIds]);

  return (
    <EditorContextProvider
      value={value}
      onChange={onChange}
      newValue={newValue}
      editorType="editor"
      displayDate={displayDate}
      components={{
        ...disableDefaultComponents ? {} : filteredDefaultComponents,
        ...components ?? {},
      }}
    >
      <EditorInfoContextProvider value={editorInfoContext}>
        <EditorComponentsProvider contexts={unitContext}>
          <S.Container className={className}>
            <S.Header>
              <Header />
            </S.Header>
            <S.Body>
              <S.Sider width={leftSidebarWidth} ref={leftSidebarRef}>
                <S.Panel>
                  <TreeList />
                </S.Panel>
                <S.Resizer right onMouseDown={startLeftSidebarResizing} />
              </S.Sider>
              <S.Canvas id="canvas" width={width} className={className} />
              <S.Sider width={rightSidebarWidth} ref={rightSidebarRef}>
                <S.Resizer left onMouseDown={startRightSidebarResizing} />
                <S.Panel>
                  <Inspector />
                </S.Panel>
              </S.Sider>
            </S.Body>
          </S.Container>
        </EditorComponentsProvider>
      </EditorInfoContextProvider>
    </EditorContextProvider>
  );
}
